import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Settings, Menu, X, LogOut, ChevronDown,
  Shield, MessageSquare
} from 'lucide-react';
import superAdminService from '../../services/superadmin/superAdminService';
import { getImageUrl } from '../../utils/imageHelper';

const SuperAdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Fetch profile data for header (Super Admin only)
  const fetchProfileData = async () => {
    const token = localStorage.getItem('authToken');

    if (!token || !token.trim()) {
      console.warn('⚠️ No token found - skipping super admin profile fetch');
      const userEmail = localStorage.getItem('userEmail');
      setUserName(userEmail?.split('@')[0] || 'Super Admin');
      return;
    }

    try {
      const profile = await superAdminService.getProfile();
      setProfileImage(profile.profile_image);
      setUserName(profile.full_name);
    } catch (error: any) {
      console.error('Error fetching super admin profile:', error);
      if (error?.response?.status === 401) {
        console.warn('⚠️ 401 error - token may be invalid');
        return;
      }
      const userEmail = localStorage.getItem('userEmail');
      setUserName(userEmail?.split('@')[0] || 'Super Admin');
    }
  };

  useEffect(() => {
    // Small delay to allow authToken to be written after login/refresh
    const timeoutId = setTimeout(() => {
      fetchProfileData();
    }, 50);

    const handleProfileImageUpdate = () => {
      fetchProfileData();
    };

    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const basePath = '/super-admin';

  const menuItems = [
    { icon: <LayoutDashboard size={20} />, label: 'Dashboard', path: `${basePath}/dashboard` },
    { icon: <Users size={20} />, label: 'Admin Management', path: `${basePath}/admin-management` },
    { icon: <Shield size={20} />, label: 'Super Admin Profile', path: `${basePath}/profile` },
  ];

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('authToken');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-md fixed w-full top-0 z-40">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Logo & Menu Button */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-secondary-600 hover:text-primary-500"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <Link to={`${basePath}/dashboard`} className="flex items-center space-x-3">
              <img
                src="/rita_logo_2-removebg-preview.png"
                alt="Rita Logo"
                className="h-20 w-auto object-contain"
              />
              <div>
                <span className="block text-xs text-secondary-500">
                  Super Admin
                </span>
              </div>
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-4">
            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 hover:bg-secondary-50 px-3 py-2 rounded-lg transition-colors"
              >
                {/* Profile Picture or Initials */}
                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-secondary-200 flex items-center justify-center bg-white relative">
                  {profileImage && (
                    <img
                      src={getImageUrl(profileImage) || ''}
                      alt={userName}
                      className="w-full h-full object-cover absolute inset-0"
                    />
                  )}
                  <span className={`text-secondary-600 font-semibold text-sm ${profileImage ? 'hidden' : ''}`}>
                    {userName ? userName.charAt(0).toUpperCase() : 'SA'}
                  </span>
                </div>
                <span className="hidden md:block font-semibold text-secondary-900">
                  {userName || 'Super Admin'}
                </span>
                <ChevronDown size={16} className="text-secondary-600" />
              </button>

              {userMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 animate-slide-down"
                  onMouseLeave={() => setUserMenuOpen(false)}
                >
                  <Link
                    to={`${basePath}/profile`}
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-4 py-2 text-secondary-700 hover:bg-primary-50 hover:text-primary-600"
                  >
                    Profile
                  </Link>
                  <hr className="my-2" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-danger-600 hover:bg-danger-50 flex items-center space-x-2"
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-30 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0`}
      >
        <nav className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto p-4 space-y-1">
            {menuItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all ${isActive(item.path)
                  ? 'bg-primary-500 text-white shadow-green'
                  : 'text-secondary-600 hover:bg-primary-50 hover:text-primary-600'
                  }`}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Logout Button */}
          <div className="p-4 pt-0 border-t border-secondary-200">
            <button
              onClick={() => {
                handleLogout();
                setSidebarOpen(false);
              }}
              className="flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all w-full text-danger-600 hover:bg-danger-50 hover:text-danger-700"
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-6">
          <Outlet />
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default SuperAdminLayout;


